import React, { useState } from 'react';
import { Box, Typography, Chip, Collapse, Divider } from '@mui/material';

const METRICS = [
  {
    term: 'EPA (Expected Points Added)',
    abbr: 'EPA',
    category: 'Core',
    desc: 'The most important metric in modern NFL analytics. Measures how many points a play added or subtracted versus what was expected given down, distance, and field position. A 3rd-and-1 conversion adds more EPA than a 1st-and-10 gain of the same yards. EPA/play is the best single measure of offensive and defensive efficiency.',
    example: 'A QB who consistently generates +0.15 EPA/attempt is elite. Average is around 0.00.',
  },
  {
    term: 'Success Rate',
    abbr: 'SR',
    category: 'Core',
    desc: 'The percentage of plays that "succeeded" based on down and distance targets. 1st down: gained ≥40% of needed yards. 2nd down: gained ≥60%. 3rd/4th down: converted. Filters out the impact of big plays and shows consistent efficiency.',
    example: 'Top offenses run ~50%+ success rate. Average is ~44–46%.',
  },
  {
    term: 'CPOE (Completion % Over Expected)',
    abbr: 'CPOE',
    category: 'Quarterback',
    desc: 'How much a QB\'s completion rate exceeds what a model predicts given air yards, receiver location, coverage, and pressure. Separates QB accuracy from scheme and receiver quality. Built into nflverse from Next Gen Stats tracking data.',
    example: 'A +3% CPOE QB is beating expectations — elite accuracy. -3% is below average.',
  },
  {
    term: 'Explosive Play Rate',
    abbr: 'EXPL%',
    category: 'Core',
    desc: 'The percentage of plays that qualify as "explosive" — a pass play gaining ≥20 yards or a rush gaining ≥10 yards. High explosive rate indicates big-play ability and stretch-the-field offense. Distinct from success rate, which measures consistency.',
    example: 'Elite offenses hit 8–11% explosive rate. League average is ~7%.',
  },
  {
    term: 'Red Zone TD Rate',
    abbr: 'RZ TD%',
    category: 'Efficiency',
    desc: 'Percentage of plays inside the opponent\'s 20-yard line that result in a touchdown. Measures a team\'s ability to finish drives — one of the most impactful factors in scoring differential.',
    example: 'Top red zone offenses convert ~20%+ of RZ plays to TDs.',
  },
  {
    term: 'QB Pressure Rate',
    abbr: 'PRES%',
    category: 'Defense',
    desc: 'The percentage of opponent pass attempts where the defense recorded a sack or QB hit. Measures pass rush effectiveness independent of coverage. A high pressure rate disrupts timing routes and forces turnovers.',
    example: 'Elite pass rushes generate 25%+ pressure rate.',
  },
  {
    term: 'Turnover Differential',
    abbr: 'TO Diff',
    category: 'Core',
    desc: 'Takeaways minus giveaways. The single most predictive stat for win-loss record over a season — but also highly regressive year-to-year since turnovers involve significant luck. Positive TO Diff is a strong indicator of W-L record, but a poor predictor of future performance.',
    example: 'Championship teams often run +8 to +15 TO Diff.',
  },
  {
    term: 'Nerd Rating (NR)',
    abbr: 'NR',
    category: 'FFN Proprietary',
    desc: 'Football for Nerds\' proprietary positional rank. Combines efficiency (EPA/play), opportunity (snap share, volume), and impact metrics into a single weighted score per position. Rankings are relative — #1 means the best qualified player at that position in the current season. Requires ≥50% of team games played to qualify.',
    example: 'A QB ranked NR #3 of 28 is in the 90th percentile of qualified starters.',
  },
  {
    term: 'Air Yards (aDOT)',
    abbr: 'aDOT',
    category: 'Receiver',
    desc: 'Average Depth of Target — how far downfield the ball is thrown to a receiver on average. High aDOT means a receiver runs deep routes; low aDOT means they catch short passes. Neither is inherently better, but aDOT heavily influences yards-after-catch opportunity.',
    example: 'Deep threats average 14+ aDOT. Slot/check-down receivers are 5–8.',
  },
  {
    term: '3rd Down Conversion Rate',
    abbr: '3rd Conv%',
    category: 'Efficiency',
    desc: 'Percentage of 3rd down plays that result in a first down or touchdown. One of the most watched stats in football — converting 3rd downs extends drives and keeps the defense off the field.',
    example: 'Elite offenses convert 45%+ of 3rd downs. League average is ~40–42%.',
  },
];

const CATEGORY_COLORS = {
  'Core':           '#2ea84b',
  'Quarterback':    '#f5a623',
  'Defense':        '#9b59b6',
  'Receiver':       '#3498db',
  'Efficiency':     '#1abc9c',
  'FFN Proprietary':'#e74c3c',
};

export default function MetricsGlossary() {
  const [expanded, setExpanded] = useState(null);

  return (
    <Box>
      {METRICS.map((m, i) => (
        <Box key={m.abbr}>
          <Box
            onClick={() => setExpanded(expanded === m.abbr ? null : m.abbr)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 2,
              cursor: 'pointer', borderRadius: 1,
              '&:hover': { backgroundColor: 'rgba(74,112,150,0.12)' },
              transition: 'background-color 0.12s',
            }}
          >
            <Box sx={{
              width: 52, height: 52, borderRadius: 1.5, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: `${CATEGORY_COLORS[m.category]}18`,
              border: `1px solid ${CATEGORY_COLORS[m.category]}44`,
            }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, color: CATEGORY_COLORS[m.category], textAlign: 'center', lineHeight: 1.2 }}>
                {m.abbr}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#e8f0f8', lineHeight: 1.2 }}>
                {m.term}
              </Typography>
              <Chip
                label={m.category}
                size="small"
                sx={{ mt: 0.4, height: 16, fontSize: '0.58rem', fontWeight: 700,
                  bgcolor: `${CATEGORY_COLORS[m.category]}22`,
                  color: CATEGORY_COLORS[m.category],
                  border: `1px solid ${CATEGORY_COLORS[m.category]}44`,
                }}
              />
            </Box>
            <Typography sx={{ color: 'rgba(245,166,35,0.4)', fontSize: '1rem', flexShrink: 0 }}>
              {expanded === m.abbr ? '▲' : '▼'}
            </Typography>
          </Box>

          <Collapse in={expanded === m.abbr}>
            <Box sx={{ px: 3, pb: 2, pt: 0.5, backgroundColor: 'rgba(8,18,32,0.5)', borderRadius: 1, mx: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#c0d4e8', lineHeight: 1.7, mb: 1 }}>
                {m.desc}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: CATEGORY_COLORS[m.category], flexShrink: 0, mt: 0.1 }}>
                  CONTEXT:
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#8aaac8', lineHeight: 1.5 }}>
                  {m.example}
                </Typography>
              </Box>
            </Box>
          </Collapse>

          {i < METRICS.length - 1 && <Divider sx={{ borderColor: 'rgba(42,69,96,0.5)' }} />}
        </Box>
      ))}
    </Box>
  );
}
